import { Button, ButtonProps } from '@chakra-ui/react'
import { Label } from 'components/typography'
import { ONBOARDING_INITIAL_STEP } from 'constants/onboarding'
import { useIsDermatology } from 'hooks/app/landings/useIsDermatology'
import { PatientAreaPage } from 'hooks/app/patientArea/common/usePatientAreaPageTitle'
import { useIsFreeConsultation } from 'hooks/app/patientArea/onboarding/useIsFreeConsultation'
import { useIsSmartfitClient } from 'hooks/helpers/useIsSmartfitClient'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FC, useMemo } from 'react'
import { getCookies } from 'utils/cookieHelpers'
import dermatologyPathologies from 'utils/data/pathologies/dermatology-pathologies.json'
import sexualHealthPathologies from 'utils/data/pathologies/sexual-health-pathologies.json'
import { forwardParams } from 'utils/forwardParams'

const defaultPathologies = [...sexualHealthPathologies, ...dermatologyPathologies]

interface ScheduleConsultationButtonProps extends ButtonProps {
  href?: string
}

const ScheduleConsultationButton: FC<ScheduleConsultationButtonProps> = ({
  title = 'Agendar uma consulta',
  href,
  ...rest
}) => {
  const { pathname } = useRouter()
  const isAnyDermatologyPage = useIsDermatology()
  const { isFreeConsultation } = useIsFreeConsultation()
  const isSmartfit = useIsSmartfitClient()

  const actualHref = useMemo(() => {
    // /disfuncao-eretil is the old /problemas-de-erecao page (merged 25/10/2026): same consultation link.
    const pathologyPath = pathname === '/disfuncao-eretil' ? '/problemas-de-erecao' : pathname
    const isMainPathology = ['/problemas-de-erecao', '/perda-de-libido', '/ejaculacao-precoce'].includes(pathologyPath)
    const isDermatology = pathname === '/dermatologia' || pathname === '/dermatologista'
    const isPrep = pathname === '/prep'
    const isEnlargement = pathname === '/harmonizacao-peniana' || pathname.includes('enlargement')
    const isHpb = pathname === '/hpb' || pathname === '/prostata-aumentada'
    const isDermatologySpecialty = isAnyDermatologyPage && !isDermatology
    const isLoggedPage = pathname.includes('/me')
    const pathology = defaultPathologies.find(({ slug }) => slug.includes(pathologyPath.replace('/', '')))
      ?.specialtySlug
    const consultationHref = href
      ? href
      : pathname === '/psicologia-sexual-parceria'
      ? '/consultar?origin=psychology'
      : pathname === '/emagrecimento'
      ? '/onboarding-peso'
      : pathname === '/omensmaisfit' || title === 'Começar sua jornada'
      ? '/peso-onboarding'
      : isMainPathology
      ? `/consultar?origin=sexual-health&pathology=${pathology}`
      : isDermatologySpecialty
      ? `/onboarding/${pathology}/${ONBOARDING_INITIAL_STEP}`
      : isDermatology
      ? `/consultar?origin=dermatology&step=consultation`
      : isPrep
      ? 'consultar?origin=sexual-health&step=sub-selection'
      : isHpb
      ? `/onboarding/hpb/${ONBOARDING_INITIAL_STEP}`
      : isSmartfit
      ? '/consultar'
      : isEnlargement
      ? isLoggedPage
        ? `${PatientAreaPage.NEW_CONSULTATION}/penis_enlargement/choose-medium?spe=penis_enlargement`
        : `/onboarding/penis_enlargement/${ONBOARDING_INITIAL_STEP}`
      : '/consultar?origin=sexual-health'

    return forwardParams(consultationHref)
  }, [href, isAnyDermatologyPage, pathname, isSmartfit, title])

  return (
    <Link prefetch={false} href={actualHref}>
      <Button
        _hover={{
          bg: 'yellow.60'
        }}
        bg="dandelion"
        boxShadow="none"
        size="lg"
        minH="48px"
        rounded="full"
        {...rest}
      >
        <Label color="darkchoc">{isFreeConsultation ? 'Começar a consulta' : title}</Label>
      </Button>
    </Link>
  )
}

export default ScheduleConsultationButton
